import React, { useState, useEffect, useRef } from 'react';
import { createUser, updateUser } from '../services/api';
import { useToast } from '../context/ToastContext';
import './UserForm.css';

function UserForm({ user, onSuccess, onClose }) {
  const { toast } = useToast();
  const isEditing = !!user;
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    first_name: '',
    last_name: '',
    role: 'sanbee_employee',
    phone: '',
    department: '',
    employee_id: '',
    is_active: true,
  });
  
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const successTimeoutRef = useRef(null);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    return () => { if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current); };
  }, []);

  // Populate form if editing
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        password: '',
        confirmPassword: '',
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        role: user.role || 'sanbee_employee',
        phone: user.phone || '',
        department: user.department || '',
        employee_id: user.employee_id || '',
        is_active: user.is_active !== false,
      });
    }
  }, [user]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError(null);
    setFieldErrors(prev => { const next = { ...prev }; delete next[name]; return next; });
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!formData.username.trim()) errors.username = 'Username is required';
    if (!isEditing && !formData.password) errors.password = 'Password is required for new users';
    else if (formData.password && formData.password.length < 6) errors.password = 'Password must be at least 6 characters';
    if (formData.password && formData.password !== formData.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Please enter a valid email address';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    if (!validateForm()) return;

    setLoading(true);

    try {
      const dataToSend = {
        username: formData.username.trim(),
        email: formData.email.trim() || null,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        role: formData.role,
        phone: formData.phone.trim() || null,
        department: formData.department.trim() || null,
        employee_id: formData.employee_id.trim() || null,
        is_active: formData.is_active,
      };

      if (formData.password) dataToSend.password = formData.password;

      let response;
      if (isEditing) {
        response = await updateUser(user.id, dataToSend);
      } else {
        response = await createUser(dataToSend);
      }

      if (response.success) {
        setLoading(false);
        setSubmitSuccess(true);
        toast.success(isEditing ? 'User updated' : 'User created');
        successTimeoutRef.current = setTimeout(() => onSuccess(), 1200);
        return;
      }
    } catch (err) {
      if (err.details && typeof err.details === 'object') {
        setFieldErrors(err.details);
      } else {
        setError(err.message || 'Failed to save user');
      }
    } finally {
      setLoading(false);
    }
  };

  // Role options – aligned with backend User.ROLE_* and getRoleChoices()
  const roleOptions = [
    { value: 'super_admin', label: 'Super Admin', description: 'Full system access' },
    { value: 'sm_tender_management', label: 'SM Tender Management', description: 'Manage tenders, EMDs, no-go' },
    { value: 'sm_product_manager', label: 'SM Product Manager', description: 'Manage brands & products, submit technical bids' },
    { value: 'sanbee_employee', label: 'Sanbee Employee', description: 'Manage clients' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="user-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit User' : 'Add New User'}</h2>
          <button className="close-btn" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="user-form">
          {error && (
            <div className="form-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              {error}
            </div>
          )}

          {/* Account Section */}
          <div className="form-section">
            <h3 className="section-title">Account Information</h3>
            
            <div className="form-row">
              <div className="form-group field-required">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Enter username"
                  disabled={isEditing}
                  required
                  aria-required="true"
                  aria-invalid={!!fieldErrors.username}
                />
                {fieldErrors.username && <span className="field-error" role="alert">{fieldErrors.username}</span>}
                {isEditing && !fieldErrors.username && (
                  <span className="field-hint">Username cannot be changed</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="user@example.com"
                  aria-invalid={!!fieldErrors.email}
                />
                {fieldErrors.email && <span className="field-error" role="alert">{fieldErrors.email}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className={`form-group ${!isEditing ? 'field-required' : ''}`}>
                <label htmlFor="password">
                  {isEditing ? 'New Password' : 'Password'}
                </label>
                <div className="password-input">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={isEditing ? 'Leave blank to keep current' : 'Enter password'}
                    aria-invalid={!!fieldErrors.password}
                  />
                  <button 
                    type="button" 
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
                {fieldErrors.password && <span className="field-error" role="alert">{fieldErrors.password}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm password"
                  aria-invalid={!!fieldErrors.confirmPassword}
                />
                {fieldErrors.confirmPassword && <span className="field-error" role="alert">{fieldErrors.confirmPassword}</span>}
              </div>
            </div>
          </div>

          {/* Personal Info Section */}
          <div className="form-section">
            <h3 className="section-title">Personal Information</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="first_name">First Name</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="Enter first name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="last_name">Last Name</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Enter last name"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>

              <div className="form-group">
                <label htmlFor="employee_id">Employee ID</label>
                <input
                  type="text"
                  id="employee_id"
                  name="employee_id"
                  value={formData.employee_id}
                  onChange={handleChange}
                  placeholder="e.g., EMP001"
                />
              </div>
            </div>
          </div>

          {/* Role & Access Section */}
          <div className="form-section">
            <h3 className="section-title">Role & Access</h3>
            
            <div className="form-group field-required">
              <label>Role</label>
              <div className="role-options">
                {roleOptions.map((role) => (
                  <label 
                    key={role.value} 
                    className={`role-option ${formData.role === role.value ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={role.value}
                      checked={formData.role === role.value}
                      onChange={handleChange}
                    />
                    <div className="role-content">
                      <span className="role-label">{role.label}</span>
                      <span className="role-description">{role.description}</span>
                    </div>
                    <div className="role-check">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="department">Department</label>
                <input
                  type="text"
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  placeholder="e.g., Sales, Engineering"
                />
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-text">
                    Account Active
                    <small>Inactive users cannot log in</small>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-actions form-actions-sticky">
            <button type="button" className="cancel-btn" onClick={onClose} disabled={submitSuccess}>
              Cancel
            </button>
            <button type="submit" className={`submit-btn ${loading ? 'btn-loading' : ''} ${submitSuccess ? 'btn-success-state' : ''}`} disabled={loading || submitSuccess}>
              {loading ? (
                <span className="btn-spinner"></span>
              ) : submitSuccess ? (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18" aria-hidden>
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Saved
                </>
              ) : isEditing ? (
                'Update User'
              ) : (
                'Create User'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserForm;
